import { useState, useEffect, Fragment } from 'react'
import { Button } from '@/components/ui/button'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2, Trash2, Plus, Copy, Check, History, FileUp, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { formatDate, xenonSystemUrl } from '@/lib/utils'
import SystemHistoryDialog from './SystemHistoryDialog'
import BulkAddSystemsDialog from './BulkAddSystemsDialog'
import BulkRemoveSystemsDialog from './BulkRemoveSystemsDialog'
import AddSystemDialog from './AddSystemDialog'
import { obfuscateUUID } from '@/lib/incognito'
import { useSystemsByPool, useAllSystemIdsByPool, useRemoveSystem } from '@/hooks/use-systems'
import { useQueryClient } from '@tanstack/react-query'

interface SystemListProps {
  poolID: string
  poolPurpose?: string | null
  touFallback?: boolean
  isIncognito?: boolean
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 300] as const
type PageSize = typeof PAGE_SIZE_OPTIONS[number]

export default function SystemList({ poolID, poolPurpose, touFallback = false, isIncognito = false }: SystemListProps) {
  const { systems, totalCount, isLoading: loading, hasNextPage, fetchNextPage, isFetchingNextPage } = useSystemsByPool(poolID)
  const { data: allSystemIDs = [], isFetching: isFetchingAllIds } = useAllSystemIdsByPool(poolID)
  const { can } = usePermissions()
  const removeSystem = useRemoveSystem()
  const qc = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [copiedAll, setCopiedAll] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [showBulkRemove, setShowBulkRemove] = useState(false)
  const [historySystemID, setHistorySystemID] = useState<string | null>(null)
  const [systemToRemove, setSystemToRemove] = useState<string | null>(null)
  const [removeConfirmText, setRemoveConfirmText] = useState('')
  const [pageSize, setPageSize] = useState<PageSize>(100)
  const [currentPage, setCurrentPage] = useState(1)
  const { toast } = useToast()

  useEffect(() => { setCurrentPage(1) }, [pageSize])

  useEffect(() => {
    const neededForNextPage = (currentPage + 1) * pageSize
    if (neededForNextPage > systems.length && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [currentPage, pageSize, systems.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleRemoveClick = (systemID: string) => {
    setSystemToRemove(systemID)
    setRemoveConfirmText('')
  }

  const handleRemoveDialogClose = () => {
    setSystemToRemove(null)
    setRemoveConfirmText('')
  }

  const handleRemoveConfirm = () => {
    if (!systemToRemove) return
    if (removeConfirmText !== 'DELETE') return

    removeSystem.mutate(
      { poolID, systemID: systemToRemove },
      {
        onSuccess: () => {
          toast({ title: 'Success', description: 'System removed from pool' })
          handleRemoveDialogClose()
        },
        onError: (err: any) => {
          toast({
            title: 'Error',
            description: err.message || 'Failed to remove system',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const handleCopySystemId = async (systemID: string) => {
    try {
      await navigator.clipboard.writeText(systemID)
      setCopiedId(systemID)
      toast({
        title: 'Copied',
        description: 'System ID copied to clipboard',
      })

      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleCopyAllIds = async () => {
    try {
      await navigator.clipboard.writeText(allSystemIDs.join('\n'))
      setCopiedAll(true)
      toast({
        title: 'Copied',
        description: `${allSystemIDs.length} system ID${allSystemIDs.length !== 1 ? 's' : ''} copied to clipboard`,
      })
      setTimeout(() => setCopiedAll(false), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  const totalPages = Math.max(1, Math.ceil((totalCount ?? systems.length) / pageSize))
  const pagedSystems = systems.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  if (loading && systems.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Systems</CardTitle>
              <CardDescription>
                {totalCount !== undefined
                  ? `${totalCount.toLocaleString()} system${totalCount !== 1 ? 's' : ''} in this pool`
                  : systems.length > 0
                    ? `${systems.length.toLocaleString()}+ systems in this pool`
                    : 'No systems in this pool'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {systems.length > 0 && !isIncognito && (
                <Button variant="outline" size="sm" onClick={handleCopyAllIds} disabled={isFetchingAllIds}>
                  {isFetchingAllIds ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : copiedAll ? (
                    <Check className="mr-2 h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="mr-2 h-4 w-4" />
                  )}
                  Copy all IDs
                </Button>
              )}
              <AuthorizedButton authorized={can('PoolsSystemsWrite')} variant="outline" size="sm" onClick={() => setShowBulkRemove(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove Systems
              </AuthorizedButton>
              <AuthorizedButton authorized={can('PoolsSystemsWrite')} variant="outline" size="sm" onClick={() => setShowBulkAdd(true)}>
                <FileUp className="mr-2 h-4 w-4" />
                Bulk Add
              </AuthorizedButton>
              <AuthorizedButton authorized={can('PoolsSystemsWrite')} onClick={() => setShowAddForm(!showAddForm)} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add System
              </AuthorizedButton>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {systems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No systems in this pool yet</p>
              <p className="text-sm mt-2">Click "Add System" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pagedSystems.map((system) => (
                <div
                  key={system.systemID}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {isIncognito ? (
                        <p className="font-mono text-sm font-medium">{obfuscateUUID(system.systemID)}</p>
                      ) : (
                        <a
                          href={xenonSystemUrl(system.systemID)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm font-medium hover:underline flex items-center gap-1"
                        >
                          {system.systemID}
                          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                        </a>
                      )}
                      {!isIncognito && (
                        <button
                          onClick={() => handleCopySystemId(system.systemID)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                          title="Copy System ID"
                        >
                          {copiedId === system.systemID ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                          )}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                      <span>
                        Active from: <span className="font-medium">{formatDate(system.activeFrom)}</span>
                      </span>
                      {system.activeUntil && (
                        <span>
                          Until: <span className="font-medium">{formatDate(system.activeUntil)}</span>
                        </span>
                      )}
                    </div>
                    {system.assets && system.assets.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <span className="font-medium">Target Assets:</span>
                        <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-6 gap-y-0.5">
                          <span className="font-medium text-foreground/50">Type</span>
                          <span className="font-medium text-foreground/50">ID</span>
                          {system.assets.map((asset: { id: string; type: string }) => (
                            <Fragment key={asset.id}>
                              <span>{asset.type}</span>
                              <span className="font-mono">
                                {isIncognito ? obfuscateUUID(asset.id) : asset.id}
                              </span>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistorySystemID(system.systemID)}
                      title="View History"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <AuthorizedButton
                      authorized={can('PoolsSystemsWrite')}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveClick(system.systemID)}
                      className="text-destructive hover:text-destructive"
                      title="Remove System"
                    >
                      <Trash2 className="h-4 w-4" />
                    </AuthorizedButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount ?? systems.length)} of {(totalCount ?? systems.length).toLocaleString()}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Systems per page:</span>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v) as PageSize)}>
                    <SelectTrigger className="h-7 w-[70px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-sm">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage === totalPages && !hasNextPage}
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddSystemDialog
        poolID={poolID}
        poolPurpose={poolPurpose}
        touFallback={touFallback}
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
      />

      {historySystemID && (
        <SystemHistoryDialog
          systemID={historySystemID}
          onClose={() => setHistorySystemID(null)}
        />
      )}

      <BulkAddSystemsDialog
        poolID={poolID}
        poolPurpose={poolPurpose}

        touFallback={touFallback}
        open={showBulkAdd}
        onClose={() => setShowBulkAdd(false)}
        onSuccess={() => {
          setShowBulkAdd(false)
          qc.invalidateQueries({ queryKey: ['systems', poolID] })
        }}
      />

      <BulkRemoveSystemsDialog
        poolID={poolID}
        open={showBulkRemove}
        onClose={() => setShowBulkRemove(false)}
        onSuccess={() => {
          setShowBulkRemove(false)
          qc.invalidateQueries({ queryKey: ['systems', poolID] })
        }}
      />

      {/* Remove System Confirmation Dialog */}
      <AlertDialog open={!!systemToRemove} onOpenChange={(open) => !open && handleRemoveDialogClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove System</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove system <strong>{systemToRemove}</strong> from this pool?
              <br />
              <br />
              This action cannot be undone. The system will be removed from the pool.
              <br />
              <br />
              Type <strong>DELETE</strong> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6">
            <Input
              value={removeConfirmText}
              onChange={(e) => setRemoveConfirmText(e.target.value)}
              placeholder="Type DELETE"
              disabled={removeSystem.isPending}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSystem.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfirm}
              disabled={removeSystem.isPending || removeConfirmText !== 'DELETE'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeSystem.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove System
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

